import "src/js/util/map_loader";

import { BaseMap, MapCircleConfig, buildComparablesZooplaLayer } from "../../Map";
import {
  Button,
  WithSkeletonLoadingProps,
  withSkeletonLoading,
} from "@landtechnologies/components";
import { Stores, useGlobalState } from "react-migration/lib/persistence/state";
import { useCallback, useEffect, useMemo } from "react";

import SITE_MAP from "react-migration/domains/sites/apollo/li-api/queries/siteMap.gql";
import { SiteMapResponseData } from "react-migration/domains/sites/typings";
import { buildComparablesLayer } from "../../Map/layers/comparables/buildComparablesLayer";
import { buildSiteLayer } from "../../Map/layers/site/buildSiteLayer";
import { geoJsonPolyToGoogleBounds } from "src/js/util/map_util";
import logSiteCardEvent from "src/js/view/sites/logSiteCardEvent";
import { routedClient } from "react-migration/lib/persistence/apollo";
import { router } from "src/js/router/react-router";
import { useQuery } from "@apollo/client/react/hooks/useQuery";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { isZoopla } from "react-migration/domains/comparables/util/isZoopla";

type ComparablesMapContainerProps = {
  mapCircleConfig: MapCircleConfig;
  visibleIds: Set<string>;
};

export const ComparablesMapContainer = withSkeletonLoading(
  ({
    hideSkeletonLoading,
    mapCircleConfig,
    visibleIds,
  }: WithSkeletonLoadingProps & ComparablesMapContainerProps) => {
    const { t } = useTranslation();
    const [{ siteCardId }] = useGlobalState(Stores.site);
    const [, { getMeasurementSystemPreference }] = useGlobalState(Stores.user);
    const [{ filters, priceMode }] = useGlobalState(Stores.comparables);

    const { loading, data } = useQuery<SiteMapResponseData>(SITE_MAP, {
      variables: { _id: siteCardId },
      client: routedClient,
    });

    const bounds = useMemo(() => {
      if (!loading && data) {
        return geoJsonPolyToGoogleBounds(data.siteMap.geometry as GeoJSON.Polygon);
      }
    }, [loading, data]);

    const siteLatLng = useMemo(() => {
      if (!loading && data?.siteMap.location) {
        return {
          lat: data.siteMap.location.coordinates[1],
          lng: data.siteMap.location.coordinates[0],
        };
      }
    }, [loading, data?.siteMap.location]);

    const onViewOnMapClick = useCallback(() => {
      if (siteLatLng) {
        router.push(
          `map/19,${siteLatLng.lat},${siteLatLng.lng}?sidebartype=site&siteid=${siteCardId}`
        );
      }
      logSiteCardEvent({
        name: "Site Card - View Site In Map Mode Clicked",
        context: { source: "sitecard-lite" },
      });
    }, [siteLatLng, siteCardId]);

    useEffect(() => {
      if (!loading) {
        hideSkeletonLoading();
      }
    }, [loading, hideSkeletonLoading]);

    const layers = useMemo(() => {
      return [
        isZoopla(priceMode)
          ? buildComparablesZooplaLayer({
              unitPreference: getMeasurementSystemPreference(),
              visibleIds,
              priceMode,
              filterOptions: filters,
            })
          : buildComparablesLayer({
              unitPreference: getMeasurementSystemPreference(),
              visibleIds,
              priceMode,
              filterOptions: filters,
            }),
        buildSiteLayer(data?.siteMap),
      ];
    }, [data?.siteMap, priceMode, visibleIds, getMeasurementSystemPreference, filters]);

    return (
      <>
        {!loading && (
          <BaseMap
            bounds={bounds}
            centerCoordinates={siteLatLng}
            interactive
            mapCircleConfig={mapCircleConfig}
            layers={layers}
          />
        )}
        <div
          data-testid="map-buttons-overlay"
          className="atlas-flex atlas-absolute atlas-top-3 atlas-right-3"
        >
          <Button.Secondary onClick={onViewOnMapClick}>
            <i className="icon-lt-map-2-line atlas-mr-1 atlas-h-3.5 atlas-w-3.5" />
            {t("sites.card.map.view_site_on_map")}
          </Button.Secondary>
        </div>
      </>
    );
  },
  {
    showPadding: false,
  }
);
